/**
 * VehicleOverviewTab - Vehicle overview information
 *
 * Displays:
 * - Vehicle info (make, model, year, VIN, status, mileage, fuel type)
 * - Current assignments (Organization, Driver, Location)
 * - Quick actions for assignments
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2, User, MapPin, Shuffle, UserPlus, Navigation, Trash2, Car, ClipboardCheck, FileText
} from 'lucide-react';
import api from '../../../api.js';
import { EntityDetailSection, EntityInfoGrid, AssignmentCard } from '../../common/EntityDetailModal.jsx';
import DriverAssignmentModal from '../modals/DriverAssignmentModal.jsx';
import LocationAssignmentModal from '../modals/LocationAssignmentModal.jsx';
import OrganizationTransferModal from '../modals/OrganizationTransferModal.jsx';
import MapWrapper, { L } from '../../map/MapWrapper.jsx';
import CTStatusCard from './CTStatusCard.jsx';
import CTRecordingModal from '../modals/CTRecordingModal.jsx';
import CarteGriseCard from './CarteGriseCard.jsx';
import VehicleScanLabelCard from './VehicleScanLabelCard.jsx';
import VehicleFiscalCard from './VehicleFiscalCard.jsx';
import PoolPresenceCard from '../../pool/PoolPresenceCard.jsx';
import { isFlagOn } from '../../../utils/dbFlags';

// Custom vehicle marker icon
const createVehicleIcon = () => {
  return L.divIcon({
    html: `<div style="background-color: #3b82f6; width: 32px; height: 32px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.5 2.8c-.1.2-.1.4-.1.6v4.7c0 .6.4 1 1 1h2"></path>
        <circle cx="7" cy="17" r="2"></circle>
        <circle cx="17" cy="17" r="2"></circle>
      </svg>
    </div>`,
    className: 'custom-vehicle-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Status configuration — le libellé vient de vehicles.status.<code>
const STATUS_CONFIG = {
  active: { color: 'bg-green-100 text-green-800' },
  maintenance: { color: 'bg-amber-100 text-amber-800' },
  retired: { color: 'bg-gray-100 text-gray-800' },
  reserved: { color: 'bg-blue-100 text-blue-800' }
};

/**
 * VehicleLocationMap - Mini-map showing vehicle's assigned site location
 */
function VehicleLocationMap({ currentLocation, vehicle }) {
  const { t } = useTranslation();
  // Build marker from site GPS coordinates
  const mapMarkers = useMemo(() => {
    if (!currentLocation?.site) return [];

    const site = currentLocation.site;
    const lat = parseFloat(site.gps_latitude);
    const lng = parseFloat(site.gps_longitude);

    // Validate coordinates
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      return [];
    }

    return [{
      id: `vehicle-${vehicle?.id}-site-${site.id}`,
      lat: lat,
      lng: lng,
      icon: createVehicleIcon(),
      popup: (
        <div className="min-w-[180px]">
          <div className="flex items-center gap-2 mb-2">
            <Car className="w-5 h-5 text-blue-600" />
            <strong className="text-gray-900">{vehicle?.make} {vehicle?.model}</strong>
          </div>
          <p className="text-sm text-gray-600 font-medium">{vehicle?.license_plate}</p>
          <hr className="my-2" />
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-600" />
            <span className="text-sm text-gray-700">{site.site_name}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{site.address}, {site.city}</p>
          <span className={`mt-2 inline-block px-2 py-0.5 text-xs rounded-full ${
            currentLocation.location?.assignment_type === 'permanent'
              ? 'bg-green-100 text-green-800'
              : 'bg-amber-100 text-amber-800'
          }`}>
            {t(`vehicles.assignmentType.${currentLocation.location?.assignment_type || 'permanent'}`, { defaultValue: currentLocation.location?.assignment_type || 'permanent' })}
          </span>
        </div>
      )
    }];
  }, [currentLocation, vehicle]);

  // No location assigned
  if (!currentLocation?.site) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-8 text-center">
        <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">{t('vehicles.overview.map.noLocationTitle')}</p>
        <p className="text-sm text-gray-500 mt-1">
          {t('vehicles.overview.map.noLocationHint')}
        </p>
      </div>
    );
  }

  // Site has no GPS coordinates
  if (mapMarkers.length === 0) {
    return (
      <div className="bg-amber-50 rounded-lg border border-amber-200 p-8 text-center">
        <MapPin className="w-12 h-12 text-amber-400 mx-auto mb-3" />
        <p className="text-amber-800 font-medium">{t('vehicles.overview.map.noGpsTitle')}</p>
        <p className="text-sm text-amber-600 mt-1">
          {t('vehicles.overview.map.noGpsHint', { site: currentLocation.site.site_name })}
        </p>
        <p className="text-xs text-amber-500 mt-2">
          {t('vehicles.overview.map.noGpsHelp')}
        </p>
      </div>
    );
  }

  return (
    <MapWrapper
      mapKey={`vehicle-location-${vehicle?.id}`}
      markers={mapMarkers}
      zoom={14}
      height="300px"
      showControls={true}
      showFitBounds={false}
      emptyMessage={t('vehicles.overview.map.unavailable')}
    />
  );
}

export function VehicleOverviewTab({ entity, entityType, onMessage, onUpdate }) {
  const { t, i18n } = useTranslation();
  const vehicle = entity;

  // Related data state
  const [drivers, setDrivers] = useState([]);
  const [sites, setSites] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [trips, setTrips] = useState([]);

  // Modal states
  const [showDriverAssignment, setShowDriverAssignment] = useState(false);
  const [showLocationAssignment, setShowLocationAssignment] = useState(false);
  const [showOrgTransfer, setShowOrgTransfer] = useState(false);
  const [showCTRecording, setShowCTRecording] = useState(false);

  // Load related data
  const loadData = useCallback(async () => {
    if (!vehicle?.id) return;

    try {
      const [driversRes, sitesRes, orgsRes, locationsRes, tripsRes] = await Promise.all([
        api.driver.getAll(),
        api.workingSite.getAll(),
        api.organization.getAll(),
        api.vehicleLocation.getByVehicle(vehicle.id),
        api.trip.getAll()
      ]);

      setDrivers(driversRes.data || []);
      setSites(sitesRes.data || []);
      setOrganizations(orgsRes.data || []);
      setVehicleLocations(locationsRes.data || []);
      setTrips((tripsRes.data || []).filter(t => t.vehicle_id === vehicle.id));
    } catch (error) {
      console.error('Error loading vehicle overview data:', error);
    }
  }, [vehicle?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get current location - checks both vehicle_locations AND working_site_id field
  const getCurrentLocation = useCallback(() => {
    // First, check for current location in vehicle_locations table
    const location = vehicleLocations.find(loc => isFlagOn(loc.is_current));
    if (location) {
      const site = sites.find(s => s.id === location.site_id);
      return { location, site, source: 'vehicle_locations' };
    }

    // Fallback: check vehicle.working_site_id
    if (vehicle?.working_site_id) {
      const site = sites.find(s => s.id === vehicle.working_site_id);
      if (site) {
        return {
          location: { assignment_type: 'permanent' },
          site,
          source: 'working_site_id'
        };
      }
    }

    return null;
  }, [vehicleLocations, sites, vehicle?.working_site_id]);

  // Get current driver - checks both active trips AND assigned_driver_id field
  const getCurrentDriver = useCallback(() => {
    // First, check for active trip (takes precedence)
    const activeTrip = trips.find(t =>
      t.vehicle_id === vehicle?.id && (t.status === 'in_progress' || t.status === 'planned')
    );
    if (activeTrip) {
      const driver = drivers.find(d => d.id === activeTrip.driver_id);
      return driver ? { driver, trip: activeTrip, source: 'trip' } : null;
    }

    // Fallback: check vehicle.assigned_driver_id
    if (vehicle?.assigned_driver_id) {
      const driver = drivers.find(d => d.id === vehicle.assigned_driver_id);
      if (driver) {
        return {
          driver,
          trip: null, // No active trip, just permanent assignment
          source: 'permanent'
        };
      }
    }

    return null;
  }, [trips, vehicle?.id, vehicle?.assigned_driver_id, drivers]);

  // Handle assignment success
  const handleAssignmentSuccess = useCallback(() => {
    loadData();
    onUpdate?.();
  }, [loadData, onUpdate]);

  // Handle unassign driver
  const handleUnassignDriver = async (trip) => {
    const confirmed = window.confirm(t('vehicles.overview.unassignConfirm'));

    if (!confirmed) return;

    try {
      await api.trip.update(trip.id, {
        status: 'completed',
        end_date: new Date().toISOString().split('T')[0],
        end_mileage: vehicle.mileage || trip.start_mileage
      });

      onMessage?.({
        title: t('vehicles.toast.success'),
        message: t('vehicles.overview.unassigned'),
        type: 'success'
      });

      loadData();
      onUpdate?.();
    } catch (error) {
      console.error('Error unassigning driver:', error);
      onMessage?.({
        title: t('vehicles.toast.error'),
        message: t('vehicles.overview.unassignError', { error: error.message }),
        type: 'error'
      });
    }
  };

  // Get current organization
  const getCurrentOrg = useCallback(() => {
    return organizations.find(o => o.code === vehicle?.org_id);
  }, [organizations, vehicle?.org_id]);

  // Vehicle info fields
  const vehicleFields = [
    { key: 'make', label: t('vehicles.overview.fields.make'), value: vehicle?.make },
    { key: 'model', label: t('vehicles.overview.fields.model'), value: vehicle?.model },
    { key: 'year', label: t('vehicles.overview.fields.year'), value: vehicle?.year },
    { key: 'license_plate', label: t('vehicles.overview.fields.plate'), value: vehicle?.license_plate },
    { key: 'vin', label: t('vehicles.overview.fields.vin'), value: vehicle?.vin },
    {
      key: 'status',
      label: t('vehicles.overview.fields.status'),
      value: vehicle?.status,
      render: (value) => {
        const config = STATUS_CONFIG[value] || STATUS_CONFIG.active;
        return (
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${config.color}`}>
            {t(`vehicles.status.${value}`, { defaultValue: value })}
          </span>
        );
      }
    },
    { key: 'mileage', label: t('vehicles.overview.fields.mileage'), value: vehicle?.mileage ? `${vehicle.mileage.toLocaleString(i18n.language)} km` : '0 km' },
    { key: 'fuel_type', label: t('vehicles.overview.fields.fuelType'), value: vehicle?.fuel_type },
    { key: 'acquisition_type', label: t('vehicles.overview.fields.acquisitionType'), value: vehicle?.acquisition_type }
  ];

  const currentDriver = getCurrentDriver();
  const currentLocation = getCurrentLocation();
  const currentOrg = getCurrentOrg();

  const statusCfg = STATUS_CONFIG[vehicle?.status] || STATUS_CONFIG.active;

  return (
    <div className="space-y-4">
      {/* « Qui détient ce véhicule ? » — l'état COURANT, avant l'identité et les
          affectations. La garde (pool) n'est pas la dotation : celle-ci reste
          affichée dans la carte « Affectations » ci-dessous. */}
      <PoolPresenceCard kind="vehicle" id={vehicle?.id} />

      {/* ============================================================
          ROW 1: Vehicle Identity (left) + Assignments (right)
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Vehicle Identity — compact card */}
        <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{vehicle?.make} {vehicle?.model}</h3>
              <p className="text-sm text-gray-500">{vehicle?.year} · {vehicle?.vehicle_category || 'VL'}</p>
            </div>
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${statusCfg.color}`}>
              {t(`vehicles.status.${vehicle?.status}`, { defaultValue: vehicle?.status })}
            </span>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('vehicles.overview.fields.plate')}</span>
              <span className="font-mono font-bold text-gray-900">{vehicle?.license_plate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('vehicles.overview.fields.vin')}</span>
              <span className="font-mono text-xs text-gray-700 truncate ml-2">{vehicle?.vin || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('vehicles.overview.fields.mileage')}</span>
              <span className="font-semibold text-gray-900">{vehicle?.mileage ? `${vehicle.mileage.toLocaleString(i18n.language)} km` : '0 km'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('vehicles.overview.fields.fuelType')}</span>
              <span className="text-gray-800">{vehicle?.fuel_type ? t(`vehicles.fuelType.${vehicle.fuel_type}`, { defaultValue: vehicle.fuel_type }) : '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('vehicles.overview.fields.acquisition')}</span>
              <span className="text-gray-800">{vehicle?.acquisition_type ? t(`vehicles.acquisitionType.${vehicle.acquisition_type}`, { defaultValue: vehicle.acquisition_type }) : '-'}</span>
            </div>
          </div>
        </div>

        {/* Assignments — 3 stacked rows in a single column, or side-by-side on wide */}
        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Organization */}
          <AssignmentCard
            title={t('vehicles.overview.cards.organization')}
            icon={Building2}
            color="purple"
            action={{ icon: Shuffle, title: t('vehicles.overview.cards.transferOrg') }}
            onAction={() => setShowOrgTransfer(true)}
          >
            {currentOrg ? (
              <div>
                <p className="font-medium text-gray-900">{currentOrg.name}</p>
                {currentOrg.city && <p className="text-xs text-gray-500">{currentOrg.city}</p>}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">{t('vehicles.overview.cards.notAssignedF')}</p>
            )}
          </AssignmentCard>

          {/* Current Driver */}
          <AssignmentCard
            title={t('vehicles.overview.cards.driver')}
            icon={User}
            color="blue"
            action={{ icon: UserPlus, title: t('vehicles.overview.cards.assignDriver') }}
            onAction={() => setShowDriverAssignment(true)}
          >
            {currentDriver ? (
              <div>
                <p className="font-medium text-gray-900">
                  {currentDriver.driver.first_name} {currentDriver.driver.last_name}
                </p>
                <p className="text-xs text-gray-500">{currentDriver.driver.email}</p>
                <span className={`mt-1.5 inline-block px-2 py-0.5 text-xs rounded-full ${
                  currentDriver.source === 'trip' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                }`}>
                  {currentDriver.source === 'trip' ? t('vehicles.overview.cards.onMission') : t('vehicles.overview.cards.permanent')}
                </span>
                {currentDriver.trip && (
                  <button
                    onClick={() => handleUnassignDriver(currentDriver.trip)}
                    className="ml-2 text-red-400 hover:text-red-600"
                    title={t('vehicles.overview.cards.unassign')}
                  >
                    <Trash2 className="w-3.5 h-3.5 inline" />
                  </button>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">{t('vehicles.overview.cards.notAssigned')}</p>
            )}
          </AssignmentCard>

          {/* Current Location */}
          <AssignmentCard
            title={t('vehicles.overview.cards.location')}
            icon={MapPin}
            color="green"
            action={{ icon: Navigation, title: t('vehicles.overview.cards.assignLocation') }}
            onAction={() => setShowLocationAssignment(true)}
          >
            {currentLocation ? (
              <div>
                <p className="font-medium text-gray-900">{currentLocation.site?.site_name || t('vehicles.overview.cards.unknownSite')}</p>
                <p className="text-xs text-gray-500">{currentLocation.site?.city}</p>
                <span className={`mt-1.5 inline-block px-2 py-0.5 text-xs rounded-full ${
                  currentLocation.location.assignment_type === 'permanent' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                }`}>
                  {t(`vehicles.assignmentType.${currentLocation.location.assignment_type}`, { defaultValue: currentLocation.location.assignment_type })}
                </span>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">{t('vehicles.overview.cards.notAssigned')}</p>
            )}
          </AssignmentCard>
        </div>
      </div>

      {/* ============================================================
          ROW 2: CT (left) + Carte Grise (right) — side by side
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EntityDetailSection title={t('vehicles.overview.sections.inspection')} icon={ClipboardCheck}>
          <CTStatusCard vehicle={vehicle} onRecordCT={() => setShowCTRecording(true)} />
        </EntityDetailSection>

        <EntityDetailSection title={t('vehicles.overview.sections.registration')} icon={FileText}>
          <CarteGriseCard vehicle={vehicle} onUpdate={onUpdate} onMessage={onMessage} />
          {/* QR pare-brise — rendu seulement pour un rôle gestionnaire (la carte le décide). */}
          <div className="mt-3">
            <VehicleScanLabelCard vehicle={vehicle} />
          </div>
        </EntityDetailSection>
      </div>

      {/* ============================================================
          ROW 3: Fiscalité (left) + Map (right) — side by side
          ============================================================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <EntityDetailSection title={t('vehicles.overview.sections.fiscal')}>
          <VehicleFiscalCard vehicle={vehicle} />
        </EntityDetailSection>

        <EntityDetailSection title={t('vehicles.overview.sections.map')}>
          <VehicleLocationMap currentLocation={currentLocation} vehicle={vehicle} />
        </EntityDetailSection>
      </div>

      {/* Assignment Modals */}
      <DriverAssignmentModal
        isOpen={showDriverAssignment}
        onClose={() => setShowDriverAssignment(false)}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        vehicle={vehicle}
        drivers={drivers}
        currentAssignment={currentDriver}
      />

      <LocationAssignmentModal
        isOpen={showLocationAssignment}
        onClose={() => setShowLocationAssignment(false)}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        vehicle={vehicle}
        sites={sites}
        currentLocation={currentLocation}
      />

      <OrganizationTransferModal
        isOpen={showOrgTransfer}
        onClose={() => setShowOrgTransfer(false)}
        onSuccess={handleAssignmentSuccess}
        onMessage={onMessage}
        vehicle={vehicle}
        organizations={organizations}
      />

      {showCTRecording && (
        <CTRecordingModal
          isOpen={showCTRecording}
          onClose={() => setShowCTRecording(false)}
          onSuccess={() => {
            setShowCTRecording(false);
            onUpdate?.();
          }}
          onMessage={onMessage}
          vehicle={vehicle}
        />
      )}
    </div>
  );
}

export default VehicleOverviewTab;
